import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import moment from 'moment';
import { Popover } from '@material-ui/core';

import { Avatar, StyledTooltip } from '@DesignSystem/data-display';
import { GenericActionsCell } from '@DesignSystem/generic-table-cells/ActionsCell/ActionsCell';
import { DSColors } from '@design-system-outdated/constants';
import { ORGANIZATION_USER_ROLES } from '@shared/constants';
import { usePopoverState } from '@shared/hooks';
import { DSArrowDownIcon } from '@ds-icons';
import { Button, List, ListItem } from '@ds';

const EmptyCell = () => <div className="user-empty-cell" />;

const ROLES_LABEL_MAP = {
  [ORGANIZATION_USER_ROLES.ADMIN]: 'Admin',
  [ORGANIZATION_USER_ROLES.MEMBER]: 'Platform member',
  [ORGANIZATION_USER_ROLES.LLM_ONLY]: 'Opik member',
  [ORGANIZATION_USER_ROLES.VIEW_ONLY_MEMBER]: 'View-only member'
};

const ROLES_DESCRIPTION_MAP = {
  [ORGANIZATION_USER_ROLES.LLM_ONLY]:
    'Full access to Opik – an end-to-end LLM Evaluation platform',
  [ORGANIZATION_USER_ROLES.VIEW_ONLY_MEMBER]:
    'Web access to Comet, including both Experiment Management and Opik, no permission to log data',
  [ORGANIZATION_USER_ROLES.MEMBER]:
    'Full access to Comet, including both Experiment Management and Opik',
  [ORGANIZATION_USER_ROLES.ADMIN]:
    'Full access to Comet, and ability to manage users, approvals, organization settings, and billing'
};

const DateCell = ({ value }) =>
  value ? moment(value).format('DD MMM YYYY') : <EmptyCell />;

const RoleCell = ({ value, onChangeRole, disabled, isSameUser, roles }) => {
  const {
    openPopover,
    onClosePopover,
    closePopover,
    isPopoverOpened,
    anchorElement
  } = usePopoverState();

  const handleRoleSelect = newMemberRole => {
    onChangeRole(newMemberRole);
    closePopover();
  };

  return (
    <StyledTooltip
      title={isSameUser ? 'You cannot change role for yourself' : ''}
      placement="top-start"
    >
      <div className="organization-member-role-cell">
        <Button
          onClick={openPopover}
          active={isPopoverOpened}
          type="secondary"
          size="medium"
          PostfixIcon={<DSArrowDownIcon />}
          disabled={disabled}
          maxLength={500}
        >
          <span>{ROLES_LABEL_MAP[value]}</span>
        </Button>
        <Popover
          id="member-role-cell"
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left'
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left'
          }}
          PaperProps={{
            className: 'viewSelectorContainer'
          }}
          elevation={0}
          transitionDuration={0}
          open={isPopoverOpened}
          anchorEl={anchorElement}
          onClose={onClosePopover}
        >
          <List>
            {roles.map(role => (
              <ListItem
                key={role}
                onClick={() => handleRoleSelect(ORGANIZATION_USER_ROLES[role])}
                active={value === ORGANIZATION_USER_ROLES[role]}
              >
                <ListItem.Text
                  primary={ROLES_LABEL_MAP[ORGANIZATION_USER_ROLES[role]]}
                  secondary={
                    ROLES_DESCRIPTION_MAP[ORGANIZATION_USER_ROLES[role]]
                  }
                />
              </ListItem>
            ))}
          </List>
        </Popover>
      </div>
    </StyledTooltip>
  );
};

RoleCell.propTypes = {
  value: PropTypes.oneOf([
    ORGANIZATION_USER_ROLES.ADMIN,
    ORGANIZATION_USER_ROLES.MEMBER,
    ORGANIZATION_USER_ROLES.VIEW_ONLY_MEMBER,
    ORGANIZATION_USER_ROLES.LLM_ONLY
  ]).isRequired,
  onChangeRole: PropTypes.func.isRequired,
  disabled: PropTypes.bool.isRequired,
  isSameUser: PropTypes.bool.isRequired,
  roles: PropTypes.arrayOf(PropTypes.string).isRequired
};

const NameCell = ({ row }) => {
  const width = '36px';
  const avatarProps = row.avatar
    ? {
        width,
        src: row.avatar
      }
    : {
        width,
        outlined: true,
        backgroundColor: DSColors.grayColor1,
        type: 'small',
        showLetters: true
      };

  return (
    <div className="organization-users-name-cell">
      <Avatar {...avatarProps} />

      <div
        className={cx('name-cell-label', {
          'no-name': !row.username
        })}
      >
        {row.username ? row.username : <EmptyCell />}
      </div>
    </div>
  );
};

const ActionsCell = ({ onDeleteClick, disabled, disabledRemoving }) => {
  const handleClickOption = value => {
    if (value === actionOptions[0].value) {
      return onDeleteClick();
    }
  };

  const actionOptions = useMemo(() => {
    return [
      {
        label: 'Delete user',
        value: 'delete',
        disabled: disabledRemoving,
        tooltipText: disabledRemoving
          ? 'You cannot remove yourself from organization'
          : ''
      }
    ];
  }, [disabledRemoving]);

  return (
    <GenericActionsCell
      options={actionOptions}
      onClick={handleClickOption}
      disabled={disabled}
    />
  );
};

ActionsCell.propTypes = {
  onDeleteClick: PropTypes.func.isRequired,
  disabled: PropTypes.bool.isRequired,
  disabledRemoving: PropTypes.bool.isRequired
};

const userCellProptypes = {
  // eslint-disable-next-line react/no-unused-prop-types
  row: PropTypes.object.isRequired
};

NameCell.propTypes = userCellProptypes;
DateCell.propTypes = userCellProptypes;

export default { DateCell, RoleCell, NameCell, ActionsCell };
